import { ReactNode } from "react";
import { LucideIcon } from "lucide-react";
import { SEOLayout } from "@/components/layouts/SEOLayout";
import { SEOHead } from "@/components/seo/SEOHead";

export interface UtilityPageLayoutProps {
  /** Optional icon shown at top of card */
  icon?: LucideIcon;
  /** Optional small eyebrow chip above the title */
  eyebrow?: string;
  /** Card title (h1) */
  title: ReactNode;
  /** Subtitle/description shown below title */
  subtitle?: ReactNode;
  /** Card body content (forms, alerts, button rows…) */
  children: ReactNode;
  /** Optional footer slot under the card */
  footer?: ReactNode;
  /** Max width of the centered card */
  maxWidth?: "sm" | "md" | "lg";
  /** SEO */
  seoTitle?: string;
  seoDescription?: string;
  canonicalUrl?: string;
  /** When true, render without the marketing chrome (nav/footer). */
  bare?: boolean;
}

/**
 * Centered single-purpose page layout. Matches homepage design system —
 * lavender-wash background, rounded-3xl card, mkt-shadow-card, Satoshi
 * headings. Use for 404, Trial Expired, Reset Password, Accept Invitation,
 * Unsubscribe, etc.
 */
export function UtilityPageLayout({
  icon: Icon,
  eyebrow,
  title,
  subtitle,
  children,
  footer,
  maxWidth = "md",
  seoTitle,
  seoDescription,
  canonicalUrl,
  bare = false,
}: UtilityPageLayoutProps) {
  const maxW =
    maxWidth === "sm" ? "max-w-md" : maxWidth === "lg" ? "max-w-2xl" : "max-w-lg";

  const body = (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 sm:py-20 bg-gradient-to-b from-[hsl(var(--mkt-primary-soft))]/40 to-background">
      <div className={`w-full ${maxW} space-y-6`}>
        <div className="rounded-3xl bg-card border border-border/60 mkt-shadow-card p-8 sm:p-10 text-center">
          {Icon && (
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
              <Icon className="h-7 w-7 text-primary" />
            </div>
          )}
          {eyebrow && (
            <span className="inline-flex items-center rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground">
              {eyebrow}
            </span>
          )}
          <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight leading-tight">
            {title}
          </h1>
          {subtitle && (
            <div className="mt-3 text-muted-foreground leading-relaxed">
              {subtitle}
            </div>
          )}
          <div className="mt-7 text-left">{children}</div>
        </div>
        {footer && (
          <div className="text-center text-sm text-muted-foreground">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  if (bare) {
    return (
      <>
        {seoTitle && (
          <SEOHead
            title={seoTitle}
            description={seoDescription || ""}
            canonicalUrl={canonicalUrl || ""}
          />
        )}
        {body}
      </>
    );
  }

  return (
    <SEOLayout canonicalUrl={canonicalUrl || ""}>
      {seoTitle && (
        <SEOHead
          title={seoTitle}
          description={seoDescription || ""}
          canonicalUrl={canonicalUrl || ""}
        />
      )}
      {body}
    </SEOLayout>
  );
}
